import {Col, Row} from '@jetbrains/ring-ui/components/grid/grid'
import classNames from 'classnames'
import {memo} from 'react'

import {stringifyId} from '../../../../../types'
import type {ProjectId, TestNameId} from '../../../../../types'
import {formatDurationWithMilliseconds} from '../../../../../utils/dateTimeUtils'
import Link from '../../../../common/Link/Link'
import type {TestStatus} from '../../CompareBuildsPage.types'

import TestStatusText from './TestStatusText/TestStatusText'

import styles from './TestRow.css'

type Props = {
  name: string | null | undefined
  suite: string | null | undefined
  testNameId: TestNameId | null | undefined
  projectId: ProjectId | null | undefined
  status1: TestStatus | null | undefined
  status2: TestStatus | null | undefined
  duration1: number | null | undefined
  duration2: number | null | undefined
  muted1: boolean | null | undefined
  muted2: boolean | null | undefined
  changed?: boolean | null | undefined
  sourceBuildNumber: string | null | undefined
  compareBuildNumber: string | null | undefined
  wide: boolean
}

function TestRow({
  name,
  suite,
  testNameId,
  projectId,
  status1,
  status2,
  duration1,
  duration2,
  muted1,
  muted2,
  changed,
  sourceBuildNumber,
  compareBuildNumber,
  wide,
}: Props) {
  const suiteSeparateLine = !wide && suite != null && suite.length > 0
  const durationDiff =
    duration1 != null && duration2 != null && duration1 !== duration2
      ? `${duration2 > duration1 ? '+' : '-'}${formatDurationWithMilliseconds(
          Math.abs(duration2 - duration1),
        )}`
      : null
  const durationTitleLines = []

  if (duration1 != null) {
    durationTitleLines.push(
      `Duration of ${
        sourceBuildNumber != null ? `#${sourceBuildNumber}` : '1st'
      } build is ${formatDurationWithMilliseconds(duration1)}`,
    )
  }

  if (duration2 != null) {
    durationTitleLines.push(
      `Duration of ${
        compareBuildNumber != null ? `#${compareBuildNumber}` : '2nd'
      } build is ${formatDurationWithMilliseconds(duration2)}`,
    )
  }

  if (durationDiff != null) {
    durationTitleLines.push(`Difference: ${durationDiff}`)
  }

  const durationTitle = durationTitleLines.join('\n')
  return (
    <Row
      className={classNames(styles.row, {
        [styles.changed]: changed,
      })}
    >
      <Col className={styles.nameCol}>
        {suiteSeparateLine && <div className={styles.suite}>{suite}</div>}
        {testNameId != null && projectId != null ? (
          <Link
            href={`/project.html?projectId=${stringifyId(projectId)}&testNameId=${stringifyId(
              testNameId,
            )}&tab=testDetails`}
            relative
          >
            {name}
          </Link>
        ) : (
          name
        )}
        {wide && suite != null && suite.length > 0 && (
          <span className={styles.suite}> {suite}</span>
        )}
      </Col>
      <Col className={styles.statusCol}>
        {suiteSeparateLine && <br />}
        <TestStatusText status={status1} muted={muted1} buildNumber={sourceBuildNumber} />
      </Col>
      <Col className={styles.statusCol}>
        {suiteSeparateLine && <br />}
        <TestStatusText status={status2} muted={muted2} buildNumber={compareBuildNumber} />
      </Col>
      <Col className={styles.durationCol}>
        {suiteSeparateLine && <br />}
        <span title={durationTitle}>
          {(duration1 != null || duration2 != null) &&
            formatDurationWithMilliseconds(duration1 ?? duration2 ?? 0)}
        </span>
      </Col>
      <Col className={styles.durationDiffCol}>
        {suiteSeparateLine && <br />}
        <span title={durationTitle}>{durationDiff}</span>
      </Col>
    </Row>
  )
}

export default memo<Props>(TestRow)
